import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import {
  CDN_URL,
  getQuizOptionsData,
  growthBookInstances,
  PERSONALITY_TITLES,
  QUIZ_OPTIONS_DATA,
} from '../../constants';
import PlayPauseIcon from '../shared/PlayPauseIcon.jsx';
import LazyImage from '../shared/LazyImage';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackRegistrationModal } from '../../utils/analytics';
import { useDesign } from '../../contexts/DesignContext';
import { useSteps } from '../../hooks/useSteps';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';
import { useGBValue } from '../../hooks/useGBValue';
import { createTempSimpleRegistration, createModelParams } from '../../utils/api';
import { buildPaywallUrl } from '../../utils/paywall';
import generationBG from '../../assets/generationBG.png';

import ssIcon from '../../assets/ss.svg';
import smIcon from '../../assets/sm.svg';
import gfIcon from '../../assets/gf.svg';
import stepSisSvg from '../../assets/StepSis.svg';
import stepMomSvg from '../../assets/StepMom.svg';
import girlfriendSvg from '../../assets/Girlfriend.svg';
import sexFriendSvg from '../../assets/SexFriend.svg';
import iconHeart from '../../assets/valentinesDay/iconHeart.png';
import iconRose from '../../assets/valentinesDay/iconRose.png';
import premIcon from '../../assets/premIcon.svg';
import giftIcon from '../../assets/gift.svg';

// simple_registration (86ca50q0c): module-scoped dedupe for the temp-user
// creation. Step5 mounts more than once per visit (Home quiz step + /summary
// route + StrictMode), and a component ref resets on each mount, so the guard
// must live outside the component. sessionStorage persists the resolved token
// across a full remount / reload within the same tab.
let moduleTempRegistrationPromise = null;
const TEMP_REG_SESSION_KEY = 'csf_simple_reg_temp';

const generateRandomSelections = () => {
  const getRandomItem = (array) =>
    array[Math.floor(Math.random() * array.length)];
  const getRandomAge = () => Math.floor(Math.random() * (55 - 18 + 1)) + 18;

  const welcomeOptions = QUIZ_OPTIONS_DATA.welcome;
  const randomWelcome = getRandomItem(welcomeOptions);
  const selectedStyle = randomWelcome.value;

  const ethnicityOptions = QUIZ_OPTIONS_DATA.ethnicity.filter(
    (opt) => !opt.style || opt.style === selectedStyle,
  );
  const bodyTypeOptions = QUIZ_OPTIONS_DATA.bodyType.filter(
    (opt) => !opt.style || opt.style === selectedStyle,
  );
  const breastSizeOptions = QUIZ_OPTIONS_DATA.breastSize.filter(
    (opt) => !opt.style || opt.style === selectedStyle,
  );
  const buttSizeOptions = QUIZ_OPTIONS_DATA.buttSize.filter(
    (opt) => !opt.style || opt.style === selectedStyle,
  );
  const hairColorOptions = QUIZ_OPTIONS_DATA.hairColor.filter(
    (opt) => !opt.style || opt.style === selectedStyle,
  );
  const hairStyleOptions = QUIZ_OPTIONS_DATA.hairStyle.filter(
    (opt) => !opt.style || opt.style === selectedStyle,
  );
  const eyeColorOptions = QUIZ_OPTIONS_DATA.eyeColor.filter(
    (opt) => !opt.style || opt.style === selectedStyle,
  );
  const voiceOptions = QUIZ_OPTIONS_DATA.voice.filter(
    (opt) => !opt.style || opt.style === selectedStyle,
  );
  const personalityOptions = QUIZ_OPTIONS_DATA.personality;

  const randomVoice = getRandomItem(
    voiceOptions.length > 0 ? voiceOptions : QUIZ_OPTIONS_DATA.voice,
  );

  return {
    welcome: selectedStyle,
    step1: {
      ethnicity: getRandomItem(
        ethnicityOptions.length > 0
          ? ethnicityOptions
          : QUIZ_OPTIONS_DATA.ethnicity,
      ).value,
      age: getRandomAge(),
    },
    step2: {
      bodyType: getRandomItem(
        bodyTypeOptions.length > 0
          ? bodyTypeOptions
          : QUIZ_OPTIONS_DATA.bodyType,
      ).value,
      breastSize: getRandomItem(
        breastSizeOptions.length > 0
          ? breastSizeOptions
          : QUIZ_OPTIONS_DATA.breastSize,
      ).value,
      buttSize: getRandomItem(
        buttSizeOptions.length > 0
          ? buttSizeOptions
          : QUIZ_OPTIONS_DATA.buttSize,
      ).value,
    },
    step3: {
      hairColor: getRandomItem(
        hairColorOptions.length > 0
          ? hairColorOptions
          : QUIZ_OPTIONS_DATA.hairColor,
      ).value,
      hairStyle: getRandomItem(
        hairStyleOptions.length > 0
          ? hairStyleOptions
          : QUIZ_OPTIONS_DATA.hairStyle,
      ).value,
      eyeColor: getRandomItem(
        eyeColorOptions.length > 0
          ? eyeColorOptions
          : QUIZ_OPTIONS_DATA.eyeColor,
      ).value,
    },
    step4: {
      voice: randomVoice.value,
      voiceAudio: randomVoice.audio,
      personality: getRandomItem(personalityOptions).value,
    },
  };
};

// Built from the RESOLVED content config (not the static defaults) so the
// last-resort fallback follows the active nsfw/sfw variation. It only fires when
// Step0 never ran — e.g. /summary opened directly — and on the SFW arm the static
// defaults would have served NSFW media there.
const buildFallbackVideos = (content) => ({
  Realistic: `${CDN_URL}/${
    content?.['realistic-home-video'] ??
    growthBookInstances['realistic-home-video']
  }`,
  Anime: `${CDN_URL}/${
    content?.['anime-home-video'] ?? growthBookInstances['anime-home-video']
  }`,
});

const buildFallbackPosters = (content) => ({
  Realistic: `${CDN_URL}/${
    content?.['realistic-home-poster'] ??
    growthBookInstances['realistic-home-poster']
  }`,
  Anime: `${CDN_URL}/${
    content?.['anime-home-poster'] ?? growthBookInstances['anime-home-poster']
  }`,
});

const Step5 = ({ onAnswer, selections: propsSelections }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [finalSelections, setFinalSelections] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = React.useRef(null);

  // Используем selections из пропсов (приходят из Home.jsx)
  const selections = propsSelections || {};

  const isHentai = location.pathname.includes('/create-ai-hentai');
  const isHalloween = !!location.pathname.includes('/halloween');
  const isPorn = location.pathname.includes('/create-ai-porn');
  const isValentines = !!location.pathname.includes('/valentines');

  const { isNewDesign } = useDesign();
  const { isShortMode } = useSteps();
  const {
    summaryVariations,
    isPersonality,
    paywallNavType,
    content: gbContent,
  } = useGrowthBookValues();
  const fallbackVideos = useMemo(
    () => buildFallbackVideos(gbContent),
    [gbContent],
  );
  const fallbackPosters = useMemo(
    () => buildFallbackPosters(gbContent),
    [gbContent],
  );
  const ab_testing = useGBValue();
  const isSimpleRegistration = paywallNavType === 'simple_registration';
  // simple_registration (86ca50q0c): always drive the fake-generation loader —
  // it hides the "Bring to life" button entirely so the user CANNOT escape to
  // /signup before we redirect to the platform paywall. The temp registration is
  // pre-fired during the loader (in the background) and the handoff runs when the
  // fixed-duration loader completes, so the ~5s generation animation always plays
  // in full instead of being cut short the instant the temp request resolves.
  const isLoaderVariant = summaryVariations === 'loader' || isSimpleRegistration;

  const [fakeProgress, setFakeProgress] = useState(0);
  const [isFakeGenerating, setIsFakeGenerating] = useState(isLoaderVariant);

  // Marks the handoff (temp registration → paywall redirect) as in flight.
  const [isHandingOff, setIsHandingOff] = useState(false);

  useEffect(() => {
    if (isLoaderVariant) setIsFakeGenerating(true);
  }, [isLoaderVariant]);

  const hasAllSelections = useCallback(
    (selectionsData) => {
      // Проверка step1
      const hasStep1 =
        selectionsData?.welcome &&
        selectionsData?.step1?.ethnicity &&
        (selectionsData?.step1?.age || selectionsData?.step4?.age);

      // Проверка step2 - может быть либо объединенный step2, либо разделенный (step2_body, step2_breast, step2_ass)
      const hasStep2Combined =
        selectionsData?.step2?.bodyType &&
        selectionsData?.step2?.breastSize &&
        selectionsData?.step2?.buttSize;

      const hasStep2Separated =
        selectionsData?.step2_body?.bodyType &&
        selectionsData?.step2_breast?.breastSize &&
        selectionsData?.step2_ass?.buttSize;

      const hasStep2 = hasStep2Combined || hasStep2Separated;

      const commonSelections = hasStep1 && hasStep2;

      // ВАЖНО: Определяем режим по ФАКТИЧЕСКИМ данным, а не по текущему GrowthBook значению!
      // Если есть step4_5 (kink) - значит квиз проходили в SHORT режиме
      // Если есть step3 и step4 - значит квиз проходили в BASE режиме
      const hasStep4_5 = !!selectionsData?.step4_5?.kink;
      const hasStep3AndStep4 =
        selectionsData?.step3?.hairColor &&
        selectionsData?.step3?.hairStyle &&
        selectionsData?.step3?.eyeColor &&
        selectionsData?.step4?.voice &&
        selectionsData?.step4?.voiceAudio &&
        selectionsData?.step4?.personality;

      // Если есть step4_5 - это SHORT режим
      if (hasStep4_5) {
        return commonSelections && hasStep4_5;
      }

      // Если есть step3 и step4 - это BASE режим
      if (hasStep3AndStep4) {
        return commonSelections && hasStep3AndStep4;
      }

      // Если ничего нет - selections неполные
      return false;
    },
    [], // Убираем зависимость от isShortMode!
  );

  useEffect(() => {
    if (hasAllSelections(selections)) {
      setFinalSelections(selections);
    } else if (location.pathname.includes('/summary') || location.pathname === '/signup') {
      const randomSelections = generateRandomSelections();
      setFinalSelections(randomSelections);
    }
  }, [location.pathname, selections, hasAllSelections]);

  const selectedStyle = finalSelections?.welcome || 'Realistic';

  const ethnicities = getQuizOptionsData(
    'ethnicity',
    isHentai ? 'Anime' : isPorn ? 'Realistic' : selectedStyle,
  );
  const hairColors = getQuizOptionsData(
    'hairColor',
    isHentai ? 'Anime' : isPorn ? 'Realistic' : selectedStyle,
  );
  const hairStyles = getQuizOptionsData(
    'hairStyle',
    isHentai ? 'Anime' : isPorn ? 'Realistic' : selectedStyle,
  );
  const bodyTypes = getQuizOptionsData(
    'bodyType',
    isHentai ? 'Anime' : isPorn ? 'Realistic' : selectedStyle,
  );
  const breastSizes = getQuizOptionsData(
    'breastSize',
    isHentai ? 'Anime' : isPorn ? 'Realistic' : selectedStyle,
  );
  const bootySizes = getQuizOptionsData(
    'buttSize',
    isHentai ? 'Anime' : isPorn ? 'Realistic' : selectedStyle,
  );
  const eyeColors = getQuizOptionsData(
    'eyeColor',
    isHentai ? 'Anime' : isPorn ? 'Realistic' : selectedStyle,
  );
  const personalitiesV2 = getQuizOptionsData(
    'personalityV2',
    isHentai ? 'Anime' : isPorn ? 'Realistic' : selectedStyle,
  );

  const welcome = finalSelections?.welcome;

  const getPath = (path) => {
    const prefix = isHalloween ? '/Halloween' : '';
    return `${CDN_URL}${prefix}${path}`;
  };

  function normalizeString(str) {
    // Mapping for porn and hentai selections to actual file names
    const pornMapping = {
      'College student': 'collegestudent',
      Dominatrix: 'dominatrix',
      Milf: 'milf',
      Nurse: 'nurse',
      Pornstar: 'pornstar',
      Secretary: 'secretary',
      'Step sister': 'stepsister',
      Teacher: 'teacher',
      'Yoga instructor': 'yogainstructor',
      Maid: 'maid',
      'Goth girl': 'gothgirl',
      Princess: 'princess',
    };

    const hentaiMapping = {
      Tentacles: 'tentacles',
      Teacher: 'teacher',
      'College student': 'collegeStudent',
      Nekomimi: 'nekomimi',
      Futanari: 'futanari',
      Milf: 'milf',
      Femdom: 'femdom',
      Pregnant: 'pregnant',
      Nurse: 'nurse',
      Fantasy: 'fantasy',
      Dandere: 'dandere',
      Idol: 'idol',
    };

    // Check if we're on hentai or porn route
    const isHentaiRoute =
      window.location.pathname.includes('/create-ai-hentai');
    const isPornRoute = window.location.pathname.includes('/create-ai-porn');

    if (isHentaiRoute && hentaiMapping[str]) {
      return hentaiMapping[str];
    } else if (isPornRoute && pornMapping[str]) {
      return pornMapping[str];
    }

    return str.toLowerCase().replace(/\s+/g, '');
  }

  const getSelectedVideo = useCallback(() => {
    if (isHentai)
      return getPath(
        `/prelander/createsexfriend/Hentai/${normalizeString(welcome)}.mp4`,
      );
    if (isPorn)
      return `${CDN_URL}/prelander/createsexfriend/Porn/${normalizeString(welcome)}.mp4`;
    // URL видео сохранён в localStorage из Step0 (уже с правильным GB-вариантом)
    if (finalSelections?.welcomeVideoSrc) return finalSelections.welcomeVideoSrc;
    // Fallback: дефолтные URL из констант
    return fallbackVideos[welcome] ?? fallbackVideos['Realistic'];
  }, [welcome, isHentai, isPorn, finalSelections?.welcomeVideoSrc, fallbackVideos]);

  const getSelectedPoster = useCallback(() => {
    if (isHentai) return getPath(`/Hentai/${normalizeString(welcome)}.webp`);
    if (isPorn) return `${CDN_URL}/Porn/${normalizeString(welcome)}.webp`;
    // Постер сохранён в localStorage из Step0
    if (finalSelections?.welcomePosterSrc) return finalSelections.welcomePosterSrc;
    // Fallback: дефолтные URL из констант
    return fallbackPosters[welcome] ?? fallbackPosters['Realistic'];
  }, [welcome, isHentai, isPorn, finalSelections?.welcomePosterSrc, fallbackPosters]);

  // Helper functions
  const getEthnicity = useCallback(() => {
    const ethnicity = finalSelections?.step1?.ethnicity;
    return (
      ethnicities.find((e) => e.value === ethnicity) || {
        image: '/assets/ethnicity-default.png',
        text: 'Unknown',
      }
    );
  }, [finalSelections?.step1?.ethnicity, ethnicities]);

  const getBodyType = useCallback(() => {
    // Поддерживаем оба формата: step2.bodyType (обычный) и step2_body.bodyType (CARROUSEL)
    const val =
      finalSelections?.step2?.bodyType || finalSelections?.step2_body?.bodyType;
    const found = bodyTypes.find((b) => b.value === val);
    return {
      image: found?.image || '/assets/step2-body1.png',
      text: found ? t(found.text) : t('bodyType.slim'),
    };
  }, [
    finalSelections?.step2?.bodyType,
    finalSelections?.step2_body?.bodyType,
    bodyTypes,
    t,
  ]);

  const getBreastSize = useCallback(() => {
    // Поддерживаем оба формата: step2.breastSize (обычный) и step2_breast.breastSize (CARROUSEL)
    const val =
      finalSelections?.step2?.breastSize ||
      finalSelections?.step2_breast?.breastSize;
    const found = breastSizes.find((b) => b.value === val);
    return {
      image: found?.image || '/assets/step2-breast1.png',
      text: found ? t(found.text) : t('breastSize.small'),
    };
  }, [
    finalSelections?.step2?.breastSize,
    finalSelections?.step2_breast?.breastSize,
    breastSizes,
    t,
  ]);

  const getBootySize = useCallback(() => {
    const val =
      finalSelections?.step2?.buttSize || finalSelections?.step2_ass?.buttSize;
    const found = bootySizes.find((b) => b.value === val);
    return {
      image: found?.image || '/assets/step2-booty1.png',
      text: found ? t(found.text) : t('buttSize.small'),
    };
  }, [
    finalSelections?.step2?.buttSize,
    finalSelections?.step2_ass?.buttSize,
    bootySizes,
    t,
  ]);

  const getHairStyle = useCallback(() => {
    const val = finalSelections?.step3?.hairStyle;
    const found = hairStyles.find((h) => h.value === val);
    return {
      image: found?.image || '/assets/step3-hair1.png',
      text: found ? t(found.text) : t('hairStyle.straight'),
    };
  }, [finalSelections?.step3?.hairStyle, hairStyles, t]);

  const getHairColor = useCallback(() => {
    const val = finalSelections?.step3?.hairColor;
    const found = hairColors.find((h) => h.value === val);
    return {
      image: found?.image || '/assets/step3-hair1.png',
      text: found ? t(found.text) : t('hairColor.black'),
    };
  }, [finalSelections?.step3?.hairColor, hairColors, t]);

  const getEyeColor = useCallback(() => {
    const val = finalSelections?.step3?.eyeColor;
    const found = eyeColors.find((e) => e.value === val);
    return {
      image: found?.image || '/assets/step3-eye1.png',
      text: found ? t(found.text) : t('eyeColor.brown'),
    };
  }, [finalSelections?.step3?.eyeColor, eyeColors, t]);

  const getVoice = useCallback(() => {
    const voice = finalSelections?.step4?.voice;
    const voiceOption = QUIZ_OPTIONS_DATA.voice.find((v) => v.value === voice);
    return {
      text: voiceOption ? t(voiceOption.text) : 'Voice 1',
      audio: finalSelections?.step4?.voiceAudio || null,
    };
  }, [finalSelections?.step4?.voice, finalSelections?.step4?.voiceAudio, t]);

  // Legacy relationship/personality card — kept for NDG exactly as before.
  const getPersonality = useCallback(() => {
    const personality = finalSelections?.step4?.personality;
    const icons = isNewDesign
      ? {
          stepSis: (
            <img src={ssIcon} alt="Step Sister" style={{ width: '60px', height: '60px' }} />
          ),
          stepMom: (
            <img src={smIcon} alt="Step Mom" style={{ width: '60px', height: '60px' }} />
          ),
          girlfriend: (
            <img src={gfIcon} alt="Girlfriend" style={{ width: '60px', height: '60px' }} />
          ),
        }
      : {
          stepSis: (
            <div className="icon-badge"><img src={stepSisSvg} alt="Step Sister" /></div>
          ),
          stepMom: (
            <div className="icon-badge"><img src={stepMomSvg} alt="Step Mom" /></div>
          ),
          girlfriend: (
            <div className="icon-badge"><img src={girlfriendSvg} alt="Girlfriend" /></div>
          ),
          sexFriend: (
            <div className="icon-badge"><img src={sexFriendSvg} alt="Sex Friend" /></div>
          ),
        };

    return {
      icon: icons[personality] || icons.girlfriend,
      text: PERSONALITY_TITLES[personality] || PERSONALITY_TITLES.girlfriend,
    };
  }, [finalSelections?.step4?.personality, isNewDesign]);

  // New personality card (non-NDG, createsexfriend-isPersonality ON): shows the
  // selected personality's image + label, falling back to the first option.
  const getPersonalityV2 = useCallback(() => {
    const value = finalSelections?.step4?.personality;
    const found =
      personalitiesV2.find((p) => p.value === value) || personalitiesV2[0];
    return {
      image: found?.image,
      label: found?.label || '',
    };
  }, [finalSelections?.step4?.personality, personalitiesV2]);

  // Use the new visual personality card only off-NDG with the flag ON.
  const usePersonalityV2 = !isNewDesign && isPersonality;

  // Whether a personality card renders at all in BASE mode. When it doesn't,
  // the voice card is alone on the last row, so it spans both columns to avoid
  // an empty cell on the right.
  const showPersonalityCard = usePersonalityV2 || isNewDesign;

  // Получаем видео для kink (step4_5) в SHORT режиме
  const getKinkVideo = useCallback(() => {
    if (!finalSelections?.step4_5?.kink) return null;

    // Используем сохраненное видео и название из Step4_5
    const videoSrc = finalSelections?.step4_5?.kinkVideoSrc;
    const title = finalSelections?.step4_5?.kinkTitle;

    // Если данные не сохранены (старая версия), используем fallback
    if (!videoSrc || !title) {
      const selectedStyle = finalSelections?.welcome || 'Realistic';
      const kinkTitles = {
        1: 'Foot job',
        2: 'Deepthroat',
        3: 'Anal',
        4: 'Boobjob',
      };
      return {
        videoSrc:
          selectedStyle === 'Anime'
            ? `${CDN_URL}/prelander/createsexfriend/attribute_videos/kinks/kinks_Anime.mp4`
            : `${CDN_URL}/prelander/createsexfriend/attribute_videos/kinks/kinks.mp4`,
        title: kinkTitles[finalSelections.step4_5.kink] || 'Kink',
      };
    }

    return {
      videoSrc,
      title,
    };
  }, [
    finalSelections?.step4_5?.kink,
    finalSelections?.step4_5?.kinkVideoSrc,
    finalSelections?.step4_5?.kinkTitle,
    finalSelections?.welcome,
  ]);

  // Create the dummy user once per visit (deduped via the module-scoped promise +
  // sessionStorage cache above — see the note at their declaration).
  const startTempRegistration = useCallback(() => {
    if (moduleTempRegistrationPromise) return moduleTempRegistrationPromise;

    let cached = null;
    try {
      cached = sessionStorage.getItem(TEMP_REG_SESSION_KEY);
    } catch {
      // ignore storage failures
    }

    if (cached) {
      moduleTempRegistrationPromise = Promise.resolve(JSON.parse(cached));
    } else {
      moduleTempRegistrationPromise = createTempSimpleRegistration(
        finalSelections,
        ab_testing,
      )
        .then((data) => {
          if (data?.success && data?.authToken) {
            try {
              sessionStorage.setItem(TEMP_REG_SESSION_KEY, JSON.stringify(data));
            } catch {
              // ignore storage failures
            }
          }
          return data;
        })
        .catch(() => {
          // Reset so a retry can re-attempt; caller falls back to /signup on null.
          moduleTempRegistrationPromise = null;
          return null;
        });
    }
    return moduleTempRegistrationPromise;
  }, [finalSelections, ab_testing]);

  const goToSignup = useCallback(() => {
    navigate(location.pathname.replace('/summary', '/signup'));
  }, [navigate, location.pathname]);

  const handleBringToLife = useCallback(async () => {
    trackRegistrationModal();

    // Non-simple-registration: existing behaviour, untouched.
    if (!isSimpleRegistration) {
      goToSignup();
      return;
    }

    if (isHandingOff) return;
    setIsHandingOff(true);

    // simple_registration: hand off to the platform paywall with the dummy user's
    // token (auth_flow=tempLogin). Any failure falls back to /signup.
    try {
      const data = await startTempRegistration();
      if (!data?.success || !data?.authToken) {
        goToSignup();
        return;
      }

      const modelParams = finalSelections
        ? await createModelParams(finalSelections)
        : null;
      const style = finalSelections?.welcome || 'Realistic';
      const theme = ab_testing?.['createsexfriends-paywall-theme'];
      const postSignupRedirect =
        ab_testing?.['createsexfriends-post-signup-redirect'];

      const paywallUrl = buildPaywallUrl(
        data.authToken,
        'login_paywall',
        modelParams,
        style,
        undefined,
        undefined,
        undefined,
        theme,
        postSignupRedirect,
        paywallNavType,
      );

      // /paywall is a PLATFORM FRONTEND page (Next :3000 in local dev), while
      // buildPaywallUrl uses SWIPEY_BASE_URL (the API :3001 locally). Repoint the
      // redirect at the platform host — in staging/prod both resolve to the same
      // origin, so this is a no-op there. Mirrors useUpgateCheckout.js's pattern.
      const platformBase =
        process.env.REACT_APP_PLATFORM_BASE_URL ||
        process.env.REACT_APP_SWIPEY_BASE_URL;
      const url = new URL(paywallUrl);
      if (platformBase) {
        const platform = new URL(platformBase);
        url.protocol = platform.protocol;
        url.host = platform.host;
      }

      // auth_flow=tempLogin tells login-paywall to do a side-effect-free session
      // bootstrap for the dummy user.
      url.searchParams.set('auth_flow', 'tempLogin');
      url.searchParams.set('lp_source', 'createsexfriend');

      // Consume the cached token: if the user comes back and re-runs the quiz in
      // this tab, they must mint a fresh dummy for the new selections rather than
      // reuse this one.
      try {
        sessionStorage.removeItem(TEMP_REG_SESSION_KEY);
      } catch {
        // ignore storage failures
      }
      moduleTempRegistrationPromise = null;

      window.location.href = url.toString();
    } catch {
      setIsHandingOff(false);
      goToSignup();
    }
  }, [
    isSimpleRegistration,
    isHandingOff,
    goToSignup,
    startTempRegistration,
    finalSelections,
    ab_testing,
    paywallNavType,
  ]);

  // Keep a ref to the latest handoff so the once-only loader effect below always
  // invokes the current closure (avoids a stale-data redirect).
  const handleBringToLifeRef = useRef(handleBringToLife);
  handleBringToLifeRef.current = handleBringToLife;

  useEffect(() => {
    if (!isLoaderVariant || !finalSelections?.welcome) return;
    // No "already started" ref guard here on purpose: React runs this effect's
    // cleanup before every re-run (and StrictMode mounts it twice in dev), so at
    // most one interval/timeout is ever live. A ref guard would let the cleanup
    // tear the timers down and then block the re-arm, freezing the loader at 0%.

    const durationMs = 5000;
    const intervalMs = 50;
    const startedAt = Date.now();
    let done = false;

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const pct = Math.min(100, (elapsed / durationMs) * 100);
      setFakeProgress(pct);
      if (elapsed >= durationMs) clearInterval(intervalId);
    }, intervalMs);

    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      setFakeProgress(100);
      // simple_registration redirects away via window.location, so keep the overlay
      // up through the async handoff rather than flashing the revealed picture for a
      // frame. The normal loader variant reveals the result as before.
      if (!isSimpleRegistration) setIsFakeGenerating(false);
      // Call the latest handoff (via ref) so it reads current selections / GB
      // values, not whatever was captured when this once-only effect first ran.
      handleBringToLifeRef.current();
    };

    // Fixed-duration loader — the SOLE driver of the handoff for both the normal
    // loader variant and simple_registration, so the generation animation always
    // plays its full duration.
    const timeoutId = setTimeout(finish, durationMs);

    // simple_registration: pre-fire the dummy-user creation in the BACKGROUND so the
    // token is ready by the time the loader completes. We deliberately do NOT finish
    // early on resolution — startTempRegistration is deduped via a module promise +
    // sessionStorage cache and can resolve almost instantly (before the overlay even
    // paints on a repeat visit), which would skip the animation entirely. The 5s
    // timeout above drives the handoff; handleBringToLife awaits this same, by-then
    // resolved promise (no added redirect latency) and falls back to /signup on failure.
    if (isSimpleRegistration) {
      startTempRegistration();
    }

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaderVariant, finalSelections?.welcome]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      audioRef.current.onended = () => setIsPlaying(false);
    }
  }, [isPlaying]);

  if (!finalSelections?.welcome) {
    return (
      <div className="quizContentWrapper lastStep">
        <QuizContent>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <p>{t('loading')}</p>
          </div>
        </QuizContent>
      </div>
    );
  }

  const renderFakeGenerationOverlay = () => (
    <div className="fake-generation-overlay">
      <div
        className="fake-generation-overlay-bg"
        style={{ backgroundImage: `url(${generationBG})` }}
      />
      <div className="fake-generation-content">
        <div className="fake-generation-progress">
          <div
            className="fake-generation-progress-fill"
            style={{ width: `${Math.min(fakeProgress, 100)}%` }}
          />
        </div>
        <div className="fake-generation-percent">
          {Math.floor(Math.min(fakeProgress, 100))}%
        </div>
        <div className="fake-generation-label">{t('setting_the_scene')}</div>
      </div>
    </div>
  );

  return (
    <div className="quizContentWrapper lastStep">
      <div className="progress-stepper-text step5-ribbon">
        <img src={giftIcon} alt="" width={12} height={12} />
        {t('limited_offer')}{' '}
        <span className="highlight-text">{t('offer_price')}</span>
        <img src={giftIcon} alt="" width={12} height={12} />
      </div>
      <img
        src={iconHeart}
        alt=""
        className="step5-icon-heart"
        aria-hidden="true"
      />
      <img
        src={iconRose}
        alt=""
        className="step5-icon-rose"
        aria-hidden="true"
      />
      <QuizContent className="step5-design">
        <QuizTitle>
          {isHalloween || isNewDesign ? (
            <>
              {t('your')}
              <span className="specialText"> {t('fantasy_slut')} </span>
              {t('is_ready')}
            </>
          ) : (
            t('fantasy_babe')
          )}
        </QuizTitle>

        <div
          className={`result-grid ${
            isShortMode ? 'result-grid-short-mode' : ''
          }`}
        >
          {isShortMode ? (
            <>
              {/* SHORT mode: Two large videos on top */}
              <div className="result-videos-row">
                <div className="result-main">
                  {isFakeGenerating ? (
                    <img
                      src={getSelectedPoster()}
                      alt=""
                      className="step0Video"
                    />
                  ) : (
                    <video
                      src={getSelectedVideo()}
                      poster={getSelectedPoster()}
                      autoPlay
                      loop
                      muted
                      preload="metadata"
                      playsInline
                      controls={false}
                      className="step0Video"
                    />
                  )}
                  {isFakeGenerating ? (
                    renderFakeGenerationOverlay()
                  ) : (
                    <div className="result-main-label">
                      {t(finalSelections.welcome)}
                    </div>
                  )}
                </div>

                {getKinkVideo() && (
                  <div className="result-main result-kink-video">
                    <video
                      src={getKinkVideo().videoSrc}
                      autoPlay
                      loop
                      muted
                      preload="metadata"
                      playsInline
                      controls={false}
                      className="step0Video"
                    />
                    <div className="result-main-label">
                      {getKinkVideo().title}
                    </div>
                  </div>
                )}
              </div>

              {/* SHORT mode: Small cards below in a row */}
              <div className="result-cards-short-mode">
                {/* Row 1 */}
                <div className="result-card result-card-ethnicity">
                  <LazyImage
                    src={getEthnicity().image}
                    alt={getEthnicity().text}
                  />
                  <span>{t(getEthnicity().text)}</span>
                </div>
                <div className="result-card result-card-age">
                  <div
                    className={`age-number ${isNewDesign ? 'specialText' : ''}`}
                  >
                    {finalSelections.step4?.age || finalSelections.step1?.age || 22}
                  </div>
                  <span>{t('years')}</span>
                </div>

                {/* Row 2 */}
                <div className="result-card">
                  <LazyImage
                    src={getBodyType().image}
                    alt={getBodyType().text}
                  />
                  <span>{getBodyType().text}</span>
                </div>
                <div className="result-card">
                  <LazyImage
                    src={getBreastSize().image}
                    alt={getBreastSize().text}
                  />
                  <span>{getBreastSize().text}</span>
                </div>

                {/* Row 3 */}
                <div className="result-card">
                  <LazyImage
                    src={getBootySize().image}
                    alt={getBootySize().text}
                  />
                  <span>{getBootySize().text}</span>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* BASE mode: Original layout */}
              {/* Left - Main Video */}
              <div className="result-main">
                {isFakeGenerating ? (
                  <img
                    src={getSelectedPoster()}
                    alt=""
                    className="step0Video"
                  />
                ) : (
                  <video
                    src={getSelectedVideo()}
                    poster={getSelectedPoster()}
                    autoPlay
                    loop
                    muted
                    preload="metadata"
                    playsInline
                    controls={false}
                    className="step0Video"
                  />
                )}
                {isFakeGenerating ? (
                  renderFakeGenerationOverlay()
                ) : (
                  <div className="result-main-label">
                    {t(finalSelections.welcome)}
                  </div>
                )}
              </div>

              {/* Right - Cards Grid */}
              <div className="result-cards">
                {/* Row 1 */}
                <div className="result-card result-card-ethnicity">
                  <LazyImage
                    src={getEthnicity().image}
                    alt={getEthnicity().text}
                  />
                  <span>{t(getEthnicity().text)}</span>
                </div>
                <div className="result-card result-card-age">
                  <div
                    className={`age-number ${isNewDesign ? 'specialText' : ''}`}
                  >
                    {finalSelections.step4?.age || finalSelections.step1?.age || 22}
                  </div>
                  <span>{t('years')}</span>
                </div>

                {/* Row 2 */}
                <div className="result-card">
                  <LazyImage
                    src={getBodyType().image}
                    alt={getBodyType().text}
                  />
                  <span>{getBodyType().text}</span>
                </div>
                <div className="result-card">
                  <LazyImage
                    src={getBreastSize().image}
                    alt={getBreastSize().text}
                  />
                  <span>{getBreastSize().text}</span>
                </div>

                {/* Row 3 */}
                <div className="result-card">
                  <LazyImage
                    src={getBootySize().image}
                    alt={getBootySize().text}
                  />
                  <span>{getBootySize().text}</span>
                </div>
                <div className="result-card">
                  <LazyImage
                    src={getHairStyle().image}
                    alt={getHairStyle().text}
                  />
                  <span>{getHairStyle().text}</span>
                </div>

                {/* Row 4 */}
                <div className="result-card">
                  <LazyImage
                    src={getHairColor().image}
                    alt={getHairColor().text}
                  />
                  <span>{getHairColor().text}</span>
                </div>
                <div className="result-card">
                  <LazyImage
                    src={getEyeColor().image}
                    alt={getEyeColor().text}
                  />
                  <span>{getEyeColor().text}</span>
                </div>

                {/* Row 5 */}
                <div
                  className={`result-card result-card-voice${
                    showPersonalityCard ? '' : ' result-card-voice-full'
                  }`}
                  onClick={togglePlay}
                >
                  {isNewDesign ? (
                    <PlayPauseIcon isPlaying={isPlaying} />
                  ) : (
                    <div
                      className={`voice-icon-wrapper ${
                        isHalloween ? 'halloween' : ''
                      }`}
                    >
                      <PlayPauseIcon isPlaying={isPlaying} />
                    </div>
                  )}

                  <span>{getVoice().text}</span>
                  {getVoice().audio && (
                    <audio
                      ref={audioRef}
                      src={getVoice().audio}
                      preload="metadata"
                    />
                  )}
                </div>
                {usePersonalityV2 ? (
                  <div className="result-card result-card-personality">
                    <LazyImage
                      src={getPersonalityV2().image}
                      alt={getPersonalityV2().label}
                    />
                    <span>{getPersonalityV2().label}</span>
                  </div>
                ) : isNewDesign ? (
                  <div className="result-card result-card-personality">
                    <div className="personality-icon-wrapper">
                      {getPersonality().icon}
                    </div>
                    <span>{t(getPersonality().text)}</span>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>

        {/* CTA Button — hidden entirely for simple_registration (isFakeGenerating
            is always true there), so the user can't click to escape the flow. The
            per-button guards below are a safety net for the first render tick. */}
        <div className="cta-container" style={isFakeGenerating || isSimpleRegistration ? { visibility: 'hidden' } : undefined}>
          <button
            className="bring-to-life-button"
            onClick={isSimpleRegistration ? undefined : handleBringToLife}
            disabled={isFakeGenerating || isHandingOff || isSimpleRegistration}
          >
            {isNewDesign ? (
              <span className="sparkle-icon">✨</span>
            ) : (
              <img src={premIcon} alt="" className="sparkle-icon" width={20} height={20} />
            )}
            <span style={isNewDesign ? { textTransform: 'uppercase' } : { fontSize: '14px', fontWeight: 500 }}>
              {isHandingOff || isSimpleRegistration
                ? t('loading')
                : isValentines
                  ? t('bring_ai')
                  : t('bring_ai_slut')}
            </span>
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step5;
